import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  Building2,
  Check,
  CheckCircle2,
  Copy,
  CupSoda,
  Loader2,
  Minus,
  Plus,
  QrCode,
  Refrigerator,
  Smartphone,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from 'app/javascript/components/ui/avatar';
import { Button } from 'app/javascript/components/ui/button';
import { Skeleton } from 'app/javascript/components/ui/skeleton';
import { ROUTES } from 'app/javascript/constants/routes';
import { trackEvent } from 'app/javascript/firebase';
import { useFridge } from 'app/javascript/queries/useFridge';
import {
  fridgeService,
  FridgeSaleError,
  newCheckoutKey,
  type FridgeProduct,
  type FridgeSale,
} from 'app/javascript/services/fridge';
import { formatCurrency } from 'app/javascript/utils/currency';

// The pending sale survives a page refresh: the signed guest cookie authorizes
// the lookup, this key remembers which sale to look up.
const saleStorageKey = (slug: string) => `bb-fridge-sale:${slug}`;

// Whitelabel storefront: the academy is the brand of this page. The student's
// parent (or anyone at the gym) scans the fridge QR, marks what they grabbed
// and pays the academy's Pix on the spot — no account, no app.
export default function CompanyFridgePage() {
  const { slug } = useParams({ from: ROUTES.COMPANY_FRIDGE });
  const { data, isLoading, isError, refetch } = useFridge(slug);
  const trackedSlugRef = useRef<string | null>(null);

  const [sale, setSale] = useState<FridgeSale | null>(null);
  const [saleLoaded, setSaleLoaded] = useState(false);

  useEffect(() => {
    if (!data?.enabled || trackedSlugRef.current === slug) return;

    trackedSlugRef.current = slug;
    trackEvent('fridge_viewed', { company_slug: slug });
  }, [data, slug]);

  // Resume a pending purchase after refresh; a settled or foreign sale just
  // clears the pointer and the shop renders. On a slug change the previous
  // academy's sale is dropped first and its in-flight lookup disowned — the
  // Pix of one gym must never surface on another gym's page.
  useEffect(() => {
    let canceled = false;
    setSale(null);
    setSaleLoaded(false);

    const stored = localStorage.getItem(saleStorageKey(slug));
    if (!stored) {
      setSaleLoaded(true);
      return;
    }

    fridgeService
      .getSale(slug, Number(stored))
      .then((existing) => {
        if (canceled) return;
        if (existing.status === 'pending') {
          setSale(existing);
        } else {
          localStorage.removeItem(saleStorageKey(slug));
        }
      })
      .catch(() => {
        if (!canceled) localStorage.removeItem(saleStorageKey(slug));
      })
      .finally(() => {
        if (!canceled) setSaleLoaded(true);
      });

    return () => {
      canceled = true;
    };
  }, [slug]);

  const startSale = useCallback(
    (created: FridgeSale) => {
      localStorage.setItem(saleStorageKey(slug), String(created.id));
      setSale(created);
      trackEvent('fridge_web_sale_created', { company_slug: slug });
    },
    [slug],
  );

  const finishSale = useCallback(() => {
    localStorage.removeItem(saleStorageKey(slug));
    setSale(null);
    void refetch();
  }, [slug, refetch]);

  if (isLoading || !saleLoaded) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 px-5" aria-busy="true" aria-label="Carregando Geladeira">
        <div className="w-full max-w-xl space-y-5 rounded-3xl border border-slate-800 bg-slate-900/70 p-6 sm:p-9">
          <Skeleton className="h-14 w-44" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </main>
    );
  }

  if (isError || !data || !data.enabled) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-950 px-5 py-10 text-white">
        <section className="w-full max-w-lg rounded-3xl border border-slate-800 bg-slate-900/80 p-7 text-center shadow-2xl shadow-black/30 sm:p-10">
          <div className="mx-auto flex size-16 items-center justify-center rounded-2xl bg-slate-800 text-slate-400">
            <Refrigerator className="size-8" aria-hidden />
          </div>
          <h1 className="mt-6 font-bebas text-4xl leading-none tracking-wide">Geladeira indisponível</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
            Esta academia ainda não ativou a Geladeira ou o endereço informado não existe.
          </p>
          <Button asChild variant="outline" className="mt-7 h-11 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white">
            <Link to={ROUTES.COMPANY_DETAILS} params={{ slug }}>
              Ver página da academia
            </Link>
          </Button>
        </section>
      </main>
    );
  }

  const { company, products } = data;
  const appUrl = `app.blackbelts://fridge/${encodeURIComponent(slug)}`;

  return (
    <main className="relative min-h-screen bg-slate-950 px-5 py-7 text-white sm:py-12">
      {/* The decorative layer clips its own glows; overflow-hidden on <main>
          would break the sticky checkout button. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-32 top-0 size-96 rounded-full bg-yellow-400/10 blur-[110px]" />
        <div className="absolute -bottom-44 -right-28 size-[480px] rounded-full bg-amber-500/[0.06] blur-[130px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.035)_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <section className="relative mx-auto w-full max-w-2xl">
        {/* No overflow-hidden here: it would break the sticky checkout button. */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900/85 shadow-2xl shadow-black/40 backdrop-blur">
          {/* The academy is the brand of this page. */}
          <header className="flex items-center gap-4 border-b border-slate-800 p-6 sm:p-8">
            <Avatar className="size-14 rounded-2xl border border-slate-700/70 bg-slate-800 sm:size-16">
              {company.logo_url ? <AvatarImage src={company.logo_url} alt={`Logo da ${company.name}`} className="object-cover" /> : null}
              <AvatarFallback className="rounded-2xl bg-slate-800 text-yellow-400">
                <Building2 className="size-7" aria-hidden />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-yellow-400">Geladeira</p>
              <h1 className="mt-1 truncate font-bebas text-3xl leading-none tracking-wide sm:text-4xl">{company.name}</h1>
              <p className="mt-1.5 text-xs text-slate-400">
                <span className="font-bebas text-sm tracking-wide text-slate-300">Pegue. <span className="text-yellow-400">Pague.</span> Treine.</span>
              </p>
            </div>
            {/* Secondary CTA for students; phones keep the footer link so the
                academy name never truncates behind a button. */}
            <a
              href={appUrl}
              className="ml-auto hidden shrink-0 items-center gap-2 rounded-lg border border-slate-700 px-3.5 py-2.5 text-xs font-bold uppercase tracking-wide text-slate-200 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50 sm:inline-flex"
            >
              <Smartphone className="size-4" aria-hidden />
              Compre pelo app
            </a>
          </header>

          <div className="p-6 sm:p-8">
            {sale ? (
              <FridgePayment slug={slug} sale={sale} onSaleChange={setSale} onFinish={finishSale} />
            ) : (
              <FridgeShop slug={slug} products={products} onCreated={startSale} />
            )}
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-slate-500">
          Pague no Pix e a academia confirma o recebimento depois. Aluno da academia?{' '}
          <a href={appUrl} className="font-semibold text-slate-400 underline-offset-2 hover:text-yellow-400 hover:underline">
            Compre pelo app
          </a>
          .
        </p>
      </section>
    </main>
  );
}

// ── Shop: [photo] [name] · [unit price] [stepper] [subtotal] ────────────────

function FridgeShop({
  slug,
  products,
  onCreated,
}: {
  slug: string;
  products: FridgeProduct[];
  onCreated: (sale: FridgeSale) => void;
}) {
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptKeyRef = useRef<string | null>(null);

  // Active products remain buyable regardless of their stock counter. A fresh
  // catalog response only removes cart entries for products the academy has
  // deactivated or deleted.
  useEffect(() => {
    const productsById = new Map(products.map((product) => [product.id, product]));
    setQuantities((current) => {
      let changed = false;
      const next: Record<number, number> = {};

      Object.entries(current).forEach(([id, quantity]) => {
        const product = productsById.get(Number(id));
        if (product && quantity > 0) next[Number(id)] = quantity;
        if (!product) changed = true;
      });

      return changed ? next : current;
    });
  }, [products]);

  const totalCents = useMemo(
    () => products.reduce((sum, product) => sum + product.price_cents * (quantities[product.id] ?? 0), 0),
    [products, quantities],
  );

  const adjust = (product: FridgeProduct, delta: number) => {
    setError(null);
    setQuantities((current) => {
      const next = Math.max((current[product.id] ?? 0) + delta, 0);
      return { ...current, [product.id]: next };
    });
  };

  const checkout = async () => {
    const items = products
      .filter((product) => (quantities[product.id] ?? 0) > 0)
      .map((product) => ({ product_id: product.id, quantity: quantities[product.id] }));
    if (items.length === 0) return;

    // The key survives a failed attempt on purpose: tapping again after a
    // request that timed out is the same purchase, and the backend answers
    // with the sale it already registered. It resets only on success.
    attemptKeyRef.current ??= newCheckoutKey();

    setSubmitting(true);
    setError(null);
    try {
      const created = await fridgeService.createSale(slug, items, attemptKeyRef.current);
      attemptKeyRef.current = null;
      onCreated(created);
    } catch (err) {
      setError(err instanceof FridgeSaleError ? err.message : 'Não foi possível registrar a compra. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (products.length === 0) {
    return (
      <p className="text-sm leading-relaxed text-slate-400">
        A geladeira ainda não tem produtos cadastrados. Fale com a academia.
      </p>
    );
  }

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Marque o que pegou</p>

      <ul className="mt-2 divide-y divide-slate-800">
        {products.map((product) => {
          const quantity = quantities[product.id] ?? 0;
          const subtotal = product.price_cents * quantity;
          return (
            <li key={product.id} className="flex items-center gap-3 py-3.5 sm:gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-700/60 bg-slate-800">
                {product.photo_url ? (
                  <img src={product.photo_url} alt="" className="size-full object-cover" />
                ) : (
                  <CupSoda className="size-5 text-slate-500" aria-hidden />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold leading-snug text-white">{product.name}</p>
                {/* Unit price rides under the name on phones; the column shows from sm:. */}
                <p className="mt-0.5 text-sm tabular-nums text-slate-400 sm:hidden">
                  {formatCurrency(product.price_cents)}
                </p>
              </div>

              <p className="hidden w-20 shrink-0 text-right text-sm tabular-nums text-slate-300 sm:block">
                {formatCurrency(product.price_cents)}
              </p>

              <div className="flex shrink-0 items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => adjust(product, -1)}
                  disabled={quantity === 0}
                  aria-label={`Tirar um ${product.name}`}
                  className="flex size-8 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50 disabled:opacity-30"
                >
                  <Minus className="size-3.5" aria-hidden />
                </button>
                <span className="w-6 text-center text-sm font-bold tabular-nums" aria-live="polite">{quantity}</span>
                <button
                  type="button"
                  onClick={() => adjust(product, 1)}
                  aria-label={`Adicionar um ${product.name}`}
                  className="flex size-8 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition-colors hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/50 disabled:opacity-30"
                >
                  <Plus className="size-3.5" aria-hidden />
                </button>
              </div>

              <p
                className={`hidden w-20 shrink-0 text-right text-sm font-bold tabular-nums sm:block ${quantity > 0 ? 'text-yellow-400' : 'text-slate-700'}`}
                aria-label={quantity > 0 ? `Subtotal de ${product.name}: ${formatCurrency(subtotal)}` : undefined}
              >
                {quantity > 0 ? formatCurrency(subtotal) : '–'}
              </p>
            </li>
          );
        })}
      </ul>

      {error ? (
        <p role="alert" className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </p>
      ) : null}

      {/* With items in the cart the CTA floats over the list while scrolling
          (sticky keeps its natural slot at the end, so full-scroll still shows
          every product above it); the gradient fades products out under it
          instead of hard-clipping them. */}
      <div
        className={
          totalCents > 0
            ? 'sticky bottom-0 z-10 -mx-6 -mb-6 bg-gradient-to-t from-slate-900 from-55% via-slate-900/90 to-transparent px-6 pb-6 pt-10 sm:-mx-8 sm:-mb-8 sm:px-8 sm:pb-8'
            : 'mt-5'
        }
      >
        <button
          type="button"
          onClick={checkout}
          disabled={totalCents === 0 || submitting}
          className={`flex min-h-14 w-full items-center rounded-xl bg-yellow-400 px-6 py-4 font-bebas text-lg uppercase tracking-wide text-slate-950 shadow-lg shadow-yellow-400/20 transition-colors hover:bg-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-40 ${totalCents === 0 ? 'justify-center' : 'justify-between'}`}
        >
          <span className="flex items-center gap-3">
            {submitting ? <Loader2 className="size-5 animate-spin" aria-hidden /> : <QrCode className="size-5" aria-hidden />}
            Pagar com Pix
          </span>
          {totalCents > 0 ? <span className="text-xl tabular-nums">{formatCurrency(totalCents)}</span> : null}
        </button>
      </div>
    </div>
  );
}

// ── Payment: Pix QR + copy code + "I paid" ──────────────────────────────────

function FridgePayment({
  slug,
  sale,
  onSaleChange,
  onFinish,
}: {
  slug: string;
  sale: FridgeSale;
  onSaleChange: (sale: FridgeSale) => void;
  onFinish: () => void;
}) {
  const [reporting, setReporting] = useState(false);
  const [copied, setCopied] = useState(false);
  // One yellow button at a time: "Copiar" carries the accent until the code
  // is copied, then hands it to "Já fiz o Pix" — the next step in the flow.
  const [copiedOnce, setCopiedOnce] = useState(false);
  const payload = sale.payment?.pix_payload ?? '';
  const reported = Boolean(sale.payment?.reported_at);

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setCopiedOnce(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard can be unavailable (http, older WebView) — the code above
      // stays selectable for a manual copy.
    }
  };

  const reportPayment = async () => {
    setReporting(true);
    try {
      onSaleChange(await fridgeService.reportPayment(slug, sale.id));
      trackEvent('fridge_web_payment_reported', { company_slug: slug });
    } catch {
      // Keep the screen; the user can try again.
    } finally {
      setReporting(false);
    }
  };

  if (reported) {
    return (
      <div className="text-center">
        <div className="mx-auto flex size-16 items-center justify-center rounded-2xl bg-emerald-400/10 text-emerald-400">
          <CheckCircle2 className="size-8" aria-hidden />
        </div>
        <p className="mt-5 font-bebas text-4xl leading-none tracking-wide">Pagamento informado!</p>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-300">
          A academia confere o recebimento e confirma a compra. Bom treino!
        </p>
        <Button
          onClick={onFinish}
          variant="outline"
          className="mt-6 h-11 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white"
        >
          Fazer outra compra
        </Button>
      </div>
    );
  }

  // The buyer scanned the fridge QR with the phone they will pay with, so a
  // payment QR on this same screen is unusable — the flow is copy the code,
  // paste it in the bank app, come back and confirm (same as the native app).
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Pague no Pix</p>

      <ul className="mt-3 space-y-1 text-sm text-slate-300">
        {sale.items.map((item) => (
          <li key={item.id} className="flex justify-between gap-3">
            <span className="truncate">{item.quantity}x {item.name}</span>
            <span className="shrink-0 tabular-nums text-slate-400">{formatCurrency(item.total_cents)}</span>
          </li>
        ))}
      </ul>

      <p className="mt-6 text-center font-bebas text-5xl leading-none tracking-wide">
        {formatCurrency(sale.total_cents)}
      </p>

      <div className="mt-5 rounded-2xl border border-slate-700/60 bg-slate-950/60 p-4">
        <p
          aria-label="Código Pix copia e cola"
          className="select-all break-all font-mono text-xs leading-5 text-slate-300"
        >
          {payload}
        </p>
        <button
          type="button"
          onClick={copyPayload}
          className={`mt-4 flex min-h-12 w-full items-center justify-center gap-3 rounded-xl px-5 py-3 font-bebas text-lg uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
            copiedOnce
              ? 'border border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white'
              : 'bg-yellow-400 text-slate-950 shadow-lg shadow-yellow-400/20 hover:bg-yellow-300'
          }`}
        >
          {copied ? <Check className="size-5 text-emerald-500" aria-hidden /> : <Copy className="size-5" aria-hidden />}
          {copied ? 'Código copiado!' : copiedOnce ? 'Copiar de novo' : 'Copiar código Pix'}
        </button>
      </div>

      <p className="mt-4 text-center text-sm leading-relaxed text-slate-400">
        Cole o código no app do seu banco e finalize o pagamento.
      </p>

      <button
        type="button"
        onClick={reportPayment}
        disabled={reporting}
        className={`mt-4 flex min-h-14 w-full items-center justify-center gap-3 rounded-xl px-5 py-4 font-bebas text-lg uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:cursor-not-allowed disabled:opacity-40 ${
          copiedOnce
            ? 'bg-yellow-400 text-slate-950 shadow-lg shadow-yellow-400/20 hover:bg-yellow-300'
            : 'border border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white'
        }`}
      >
        {reporting ? <Loader2 className="size-5 animate-spin" aria-hidden /> : <CheckCircle2 className="size-5" aria-hidden />}
        Já fiz o Pix
      </button>
      <p className="mt-3 text-center text-xs leading-relaxed text-slate-500">
        Depois de pagar, toque em “Já fiz o Pix” para avisar a academia.
      </p>
    </div>
  );
}
